// The official LeadRails brand mark, in all the variants the product needs.
//
// Variants:
//   "mark"             — the LR symbol alone with the navy + cyan rail bars.
//                        Square-ish. Use when space is tight or alongside the
//                        wordmark in text (e.g. sidebar header + brand name).
//   "lockup-horizontal"— LR mark + "LeadRails" wordmark, side by side. Use in
//                        wide brand contexts (marketing page header, email
//                        signature). Wider than tall.
//   "lockup-vertical"  — LR mark on top, "LeadRails" wordmark below. Use in
//                        narrow vertical contexts (centered login card, splash).
//   "wordmark"         — Just "LeadRails" with the integrated rail-track L
//                        accent. Use when the mark already appears nearby.
//   "app-icon"         — Rounded-square dark-navy badge with the LR mark and
//                        rails. Use for in-app PWA-install prompts and as a
//                        fallback for the favicon if a route needs to render
//                        the app icon visually. Has built-in padding.
//
// Implementation notes:
//   - All variants are served as static SVG from /brand/*.svg. The files are
//     small (<1kb each) and the public/ path is cacheable forever via vite.
//   - The `height` prop is the controlling dimension. The width auto-scales
//     to the variant's natural aspect ratio.
//   - Custom branding (agency white-label) lives elsewhere — see
//     `apps/admin-ui/src/lib/branding.ts` and the `useAgencyBranding` hook.
//     This component is the LeadRails default the white-label may override.

import { cn } from "@/lib/utils";

export type LogoVariant =
  | "mark"
  | "lockup-horizontal"
  | "lockup-vertical"
  | "wordmark"
  | "app-icon";

interface LogoProps {
  variant?: LogoVariant;
  /** Controlling dimension in px. Defaults: 24 for mark/app-icon, 32 for lockups. */
  height?: number;
  className?: string;
  /** Accessible label. Set to "" for purely decorative uses next to readable text. */
  alt?: string;
}

const SRC: Record<LogoVariant, string> = {
  mark: "/brand/mark.svg",
  "lockup-horizontal": "/brand/lockup-horizontal.svg",
  "lockup-vertical": "/brand/lockup-vertical.svg",
  wordmark: "/brand/wordmark.svg",
  "app-icon": "/brand/app-icon.svg",
};

const DEFAULT_HEIGHT: Record<LogoVariant, number> = {
  mark: 24,
  "lockup-horizontal": 32,
  "lockup-vertical": 80,
  wordmark: 32,
  "app-icon": 40,
};

export function Logo({
  variant = "lockup-horizontal",
  height,
  className,
  alt = "LeadRails",
}: LogoProps) {
  const h = height ?? DEFAULT_HEIGHT[variant];
  return (
    <img
      src={SRC[variant]}
      alt={alt}
      height={h}
      style={{ height: `${h}px`, width: "auto" }}
      className={cn("select-none", className)}
      draggable={false}
    />
  );
}
